import * as React from "react";
import { fetchPostCodes, IPostCode } from "@nju/data-access-portal-wwt";
import { Combobox } from "@nju/form";
import { Loader } from "@nju/ui";

export const formatPostCode = (v: string) => {
  if (/^\d{5}$/.test(v)) {
    return `${v.slice(0, 2)}-${v.slice(2)}`;
  }
  return v;
};

export function PostCodeAutocomplete({
  cityId,
  isDisabled,
  onChange,
  onNotFound,
  state,
  defaultValue,
}: {
  cityId?: number;
  isDisabled?: boolean;
  onChange: (item?: IPostCode) => void;
  onNotFound?: (error?: string) => void;
  state?: "error";
  defaultValue?: string;
}) {
  const [inputValue, setInputValue] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [data, setData] = React.useState<IPostCode[]>([]);

  async function validatePostcode({
    cityId,
    query,
  }: {
    cityId: number;
    query: string;
  }) {
    setIsLoading(true);
    try {
      const results = (await fetchPostCodes({ query, cityId })).unwrap();
      if (results.length === 0) {
        onNotFound?.("kod nie istnieje.");
      }
    } finally {
      setIsLoading(false);
    }
  }

  const onSelect = ([element]: IPostCode[]) => {
    if (element) {
      cityId && validatePostcode({ cityId, query: element.postCode });
      onChange(element);
    }
  };

  React.useEffect(() => {
    const normalizedInput = inputValue.replace("-", "");
    const found = data.find((item) => {
      return item.postCode.replace("-", "") === normalizedInput;
    });
    if (found && inputValue.length >= 5 && found.postCode !== defaultValue) {
      onSelect([found]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, data, defaultValue]);

  React.useEffect(() => {
    async function action(signal: AbortSignal) {
      const cleanedInputValue = inputValue.replaceAll("-", "");
      if (cleanedInputValue.length < 3) {
        return;
      }
      try {
        setIsLoading(true);
        const list = (
          await fetchPostCodes({ query: cleanedInputValue, signal })
        ).unwrap();
        setData(list);
      } catch {
        setIsLoading(false);
      } finally {
        setIsLoading(false);
      }
    }

    const abortController = new AbortController();
    action(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [inputValue]);

  return (
    <Combobox
      defaultValue={
        defaultValue ? [{ postCode: defaultValue } as IPostCode] : undefined
      }
      inputDisplayValue={([v]) => {
        return formatPostCode(v?.postCode) || "";
      }}
      isDisabled={isDisabled}
      itemComparator={(first, second) => {
        return first.postCode === second.postCode;
      }}
      items={data}
      key={defaultValue}
      label="kod pocztowy"
      listColorVariant="gray"
      listItemDisplayValue={(v) => {
        return formatPostCode(v.postCode);
      }}
      onInputChange={setInputValue}
      onSelect={onSelect}
      openOnFocus={false}
      state={state}
      suffix={isLoading ? <Loader /> : undefined}
      uniqueItemId={(item) => {
        return item.postCode;
      }}
    />
  );
}
